import styled from 'styled-components';
import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import React, { useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle } from 'react';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import isDefinedAndNotNull from '@userzoomlibraries/toolkit-js-utils/is-defined-and-not-null';
import useComparePrevious from '@userzoomlibraries/toolkit-react-utils/use-compare-previous';
import usePrevious from '@userzoomlibraries/toolkit-react-utils/use-previous';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import { N as NativeSelectOption, a as NativeSelectContext } from './index.logic.js';
import { N as NativeSelectOptionGroup } from './index.logic2.js';
import Icon from '@userzoomlibraries/toolkit-icon';
import { a as angleDown } from './index.js';
import { jsx, jsxs } from 'react/jsx-runtime';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var NativeSelectArrow$1 = function NativeSelectArrow(_ref) {
  var className = _ref.className,
      disabled = _ref.disabled;
  var iconName = 'angle-down';
  var iconFallback = angleDown;

  var arrowContent = /*#__PURE__*/jsx(Icon, {
    "aria-hidden": "true",
    "aria-disabled": disabled,
    className: "gz-native-select__arrow",
    name: iconName,
    fallback: iconFallback
  });

  return arrowContent && /*#__PURE__*/jsx("div", {
    className: "gz-native-select__arrow-container ".concat(className),
    children: arrowContent
  });
};

NativeSelectArrow$1.displayName = 'Gearz.NativeSelect.Arrow';
NativeSelectArrow$1.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool
};
NativeSelectArrow$1.defaultProps = {
  className: '',
  disabled: false
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var NativeSelectArrow = styled(NativeSelectArrow$1).withConfig({
  componentId: "gz-select-1679042064662__sc-w94cvx-0"
})(["cursor:pointer;display:flex;height:20px;position:absolute;right:6px;text-align:center;top:calc(50% - 10px);width:20px;z-index:1;.gz-native-select__arrow{height:20px;width:20px;&[aria-disabled=\"true\"]{cursor:not-allowed;}}"]);
NativeSelectArrow.displayName = NativeSelectArrow$1.displayName;
NativeSelectArrow.propTypes = NativeSelectArrow$1.propTypes;
NativeSelectArrow.defaultProps = NativeSelectArrow$1.defaultProps;

var _excluded = ["autoFocus", "className", "children", "defaultValue", "disabled", "id", "invalid", "multiple", "name", "rows", "value", "onChange", "onFocus", "onBlur"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var NativeSelect$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var autoFocus = _ref.autoFocus,
      className = _ref.className,
      children = _ref.children,
      defaultValue = _ref.defaultValue,
      disabled = _ref.disabled,
      id = _ref.id,
      invalid = _ref.invalid,
      multiple = _ref.multiple,
      name = _ref.name,
      rows = _ref.rows,
      value = _ref.value,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var ariaAttributes = useAriaAttributes(otherProps, {
    excludedAttributes: ['aria-invalid', 'aria-disabled']
  });
  var dataAttributes = useDataAttributes(otherProps, {
    excludedAttributes: ['data-multiple']
  });
  var nativeSelectRef = useRef();

  var _useState = useState(function () {
    var finalValue = [];

    if (isDefinedAndNotNull(defaultValue)) {
      finalValue = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
    }

    if (isDefinedAndNotNull(value)) {
      finalValue = Array.isArray(value) ? value : [value];
    }

    return finalValue;
  }),
      _useState2 = _slicedToArray(_useState, 2),
      stateValue = _useState2[0],
      setStateValue = _useState2[1];

  var previousStateValue = usePrevious(stateValue);
  var isControlledSelect = useMemo(function () {
    if (isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    if (!isDefinedAndNotNull(defaultValue) && isDefinedAndNotNull(value)) {
      return true;
    }

    if (!isDefinedAndNotNull(defaultValue) && !isDefinedAndNotNull(value)) {
      return false;
    }

    console.error("Warning: \"".concat(NativeSelect$1.displayName, "\" contains a select with both value and defaultValue props.\n      Select elements must be either controlled or uncontrolled (specify either the value prop, or the defaultValue prop, but not both).\n      Decide between using a controlled or uncontrolled select element and remove one of these props. More info: https://reactjs.org/link/controlled-components%s"));
    return false;
  }, [useComparePrevious(value), useComparePrevious(defaultValue)]);
  var onChangeHandler = useCallback(function (event) {
    var selectedOptions = event.target.selectedOptions;
    var selectedValues = Array.from(selectedOptions).map(function (each) {
      return each.value;
    });

    if (!isControlledSelect) {
      setStateValue(selectedValues);
    }

    var selectedValuesToSend = multiple ? selectedValues : selectedValues.at(0);
    onChange({
      value: selectedValuesToSend,
      event: event
    });
  }, [multiple, isControlledSelect, onChange]);
  useEffect(function () {
    // We set the defaultValue only when we don't have any selected option
    if (isDefinedAndNotNull(defaultValue) && stateValue.length === 0 && previousStateValue.length === 0) {
      var defaultValueAsArray = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
      setStateValue(defaultValueAsArray);
    }
  }, [defaultValue, previousStateValue, stateValue]);
  useEffect(function () {
    if (isDefinedAndNotNull(value)) {
      var valueAsArray = Array.isArray(value) ? value : [value];
      setStateValue(valueAsArray);
    }
  }, [value]);
  useImperativeHandle(ref, function () {
    return nativeSelectRef.current;
  });
  var valueToRender = multiple ? stateValue : stateValue.at(0);
  var providerValue = {
    defaultValue: defaultValue,
    value: stateValue
  };
  return /*#__PURE__*/jsx(NativeSelectContext.Provider, {
    value: providerValue,
    children: /*#__PURE__*/jsxs("div", {
      className: "gz-native-select-wrapper ".concat(className),
      "aria-disabled": disabled,
      children: [/*#__PURE__*/jsx("select", _objectSpread(_objectSpread(_objectSpread({
        ref: nativeSelectRef // eslint-disable-next-line jsx-a11y/no-autofocus
        ,
        autoFocus: autoFocus,
        className: "gz-native-select",
        disabled: disabled,
        id: id,
        multiple: multiple,
        name: name,
        size: rows,
        value: valueToRender,
        onChange: onChangeHandler,
        onFocus: onFocus,
        onBlur: onBlur,
        "data-multiple": multiple,
        "aria-disabled": disabled,
        "aria-invalid": invalid // eslint-disable-next-line react/jsx-props-no-spreading

      }, ariaAttributes), dataAttributes), {}, {
        children: children
      })), !multiple ? /*#__PURE__*/jsx(NativeSelectArrow, {}) : null]
    })
  });
});
NativeSelect$1.COMPONENT_ID = 'Gearz.NativeSelect';
NativeSelect$1.displayName = NativeSelect$1.COMPONENT_ID;
NativeSelect$1.Option = NativeSelectOption;
NativeSelect$1.OptionGroup = NativeSelectOptionGroup;
NativeSelect$1.propTypes = {
  // eslint-disable-next-line max-len
  autoFocus: PropTypes.boolWithDescription('Flag to focus the native select on page load. The autoFocus prop should not be used, as [it can reduce usability and accessibility for users](https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/566011b89df811b95bc4e99799a5ea03d440411d/docs/rules/no-autofocus.md).'),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Options of the native select.'),
  defaultValue: PropTypes.oneOfTypeWithDescription([PropTypes.number, PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))], 'Default value of the select.'),
  disabled: PropTypes.boolWithDescription('Flag to make the select disable.'),
  id: PropTypes.stringWithDescription('Id of the native select.'),
  invalid: PropTypes.boolWithDescription('Flag to make the select invalid.'),
  multiple: PropTypes.boolWithDescription('Flag to make the select multi select.'),
  name: PropTypes.stringWithDescription('Name of the native select.'),
  rows: PropTypes.numberWithDescription('When `multiple` is specified, this prop represents the number of rows in the list that should be visible at one time. Browsers are not required to present a select element as a scrolled list box.'),
  value: PropTypes.oneOfTypeWithDescription([PropTypes.number, PropTypes.string, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))], 'Value selected of the select. (controlled)'),
  onChange: PropTypes.funcWithDescription('Callback called when a new options is selected/deselected. `({ value, optionSelected, optionDeselected }) => {}`'),
  onFocus: PropTypes.funcWithDescription('Callback called when focus event is triggered.'),
  onBlur: PropTypes.funcWithDescription('Callback called when blur event is triggered.')
};
NativeSelect$1.defaultProps = {
  autoFocus: false,
  className: '',
  children: null,
  defaultValue: null,
  disabled: false,
  id: null,
  invalid: false,
  multiple: false,
  name: null,
  rows: null,
  value: null,
  onChange: noop,
  onFocus: noop,
  onBlur: noop
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var NativeSelect = styled(NativeSelect$1).withConfig({
  componentId: "gz-select-1679042064662__sc-urzoz9-0"
})(["align-items:center;background-color:#fff;display:flex;flex:1;position:relative;width:100%;&[aria-disabled=\"true\"]{cursor:not-allowed;opacity:0.24;user-select:none;}.gz-native-select{appearance:none;background-color:transparent;border:1px solid #333;border-radius:4px;border-radius:4px;cursor:pointer;height:100%;min-height:40px;padding:6px 30px 6px 6px;width:100%;z-index:2;&:not([aria-disabled=\"true\"]){&[aria-invalid=\"true\"]{border:1px solid #f00314;}&:focus-within{outline:2px solid #314fff;outline-offset:2px;}}&[aria-disabled=\"true\"]{cursor:not-allowed;opacity:0.24;user-select:none;}}", ""], function (props) {
  return getThemeByProps({
    componentId: NativeSelect$1.COMPONENT_ID,
    props: props
  });
});
NativeSelect.displayName = NativeSelect$1.displayName;
NativeSelect.propTypes = NativeSelect$1.propTypes;
NativeSelect.defaultProps = NativeSelect$1.defaultProps;

export { NativeSelect as N };
